import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getParameterLookupId, getParameters } from '../../services/catalogService';
import { getLlmModels } from '../../services/llmModelsService';
import { createMappingJob } from '../../services/mappingJobsService';

const OMOP_MAPPING_ACTIONS_COPY = {
  de: {
    validateTooltip: 'Mapping als korrekt validieren',
    validate: 'Validieren',
    rejectTooltip: 'Mapping ablehnen',
    reject: 'Ablehnen',
    deferTooltip: 'Entscheidung zurückstellen',
    defer: 'Zurückstellen',
    notePlaceholder: 'Notiz zur Entscheidung (optional)',
    generateTooltip: 'AI-gestütztes Mapping generieren lassen',
    generating: 'Generiere...',
    aiAssistant: 'KI-Assistent',
    criticTooltip: 'Geben Sie der KI konkrete Hinweise für einen besseren Mapping-Vorschlag',
    critic: 'KI-Vorschlag verbessern',
  },
  en: {
    validateTooltip: 'Validate the mapping as correct',
    validate: 'Validate',
    rejectTooltip: 'Reject the mapping',
    reject: 'Reject',
    deferTooltip: 'Defer the decision',
    defer: 'Defer',
    notePlaceholder: 'Note about the decision (optional)',
    generateTooltip: 'Generate an AI-supported mapping suggestion',
    generating: 'Generating...',
    aiAssistant: 'AI assistant',
    criticTooltip: 'Give the AI concrete hints for a better mapping suggestion',
    critic: 'Improve AI suggestion',
  },
  pt: {
    validateTooltip: 'Validar o mapeamento como correto',
    validate: 'Validar',
    rejectTooltip: 'Rejeitar o mapeamento',
    reject: 'Rejeitar',
    deferTooltip: 'Adiar a decisão',
    defer: 'Adiar',
    notePlaceholder: 'Nota sobre a decisão (opcional)',
    generateTooltip: 'Gerar uma sugestão de mapeamento com IA',
    generating: 'A gerar...',
    aiAssistant: 'Assistente IA',
    criticTooltip: 'Dê indicações concretas à IA para uma sugestão de mapeamento melhor',
    critic: 'Melhorar sugestão da IA',
  },
};

/**
 * OmopMappingActions Component
 * Action buttons for OMOP mapping workflows
 * Handles validate, reject, defer, edit, generate, and feedback actions
 *
 * @param {object} props
 * @param {string} props.omopStatus - Current mapping status
 * @param {function} props.onValidate - Callback for validate/accept action, receives an optional note
 * @param {function} props.onReject - Callback for reject action, receives an optional note
 * @param {function} props.onDefer - Callback for defer action, receives an optional note
 * @param {function} props.onEdit - Callback for edit/manual mapping action
 * @param {function} props.onGenerateMapping - Callback for AI mapping generation
 * @param {function} props.onOpenCritic - Callback to open feedback dialog
 * @param {boolean} props.isProcessing - Whether an action is in progress
 * @param {boolean} props.hasAiMapping - Whether AI mapping exists
 */
const OmopMappingActions = ({
  omopStatus,
  onValidate,
  onReject,
  onDefer,
  onEdit,
  onGenerateMapping,
  onOpenCritic,
  isProcessing = false,
  hasAiMapping = false
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_MAPPING_ACTIONS_COPY[language];
  const [note, setNote] = useState('');

  // Determine which actions are available based on status
  // Eine bereits getroffene Entscheidung bleibt aenderbar (#99 AK5) -- die
  // Buttons haengen nur davon ab, ob es ueberhaupt ein Mapping zum Entscheiden
  // gibt, nicht vom aktuellen omopStatus.
  const showValidateReject = hasAiMapping;
  const showGenerate = ['unmapped', 'pending'].includes(omopStatus);
  const showCritic = hasAiMapping && omopStatus !== 'validated';

  const decide = (callback) => {
    if (!callback) {
      return;
    }
    callback(note);
    setNote('');
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Validate, Reject & Defer Group */}
      {showValidateReject && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small"
            placeholder={copy.notePlaceholder}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={isProcessing}
            inputProps={{ 'aria-label': copy.notePlaceholder }}
          />
          <ButtonGroup variant="contained" disabled={isProcessing}>
            <Tooltip title={copy.validateTooltip}>
              <Button
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => decide(onValidate)}
              >
                {copy.validate}
              </Button>
            </Tooltip>
            <Tooltip title={copy.rejectTooltip}>
              <Button
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => decide(onReject)}
              >
                {copy.reject}
              </Button>
            </Tooltip>
            <Tooltip title={copy.deferTooltip}>
              <Button
                color="inherit"
                startIcon={<PendingActionsIcon />}
                onClick={() => decide(onDefer)}
              >
                {copy.defer}
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>
      )}

      {/* Generate AI Mapping */}
      {showGenerate && (
        <Tooltip title={copy.generateTooltip}>
          <Button
            variant="contained"
            startIcon={isProcessing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AutoAwesomeIcon />}
            onClick={onGenerateMapping}
            disabled={isProcessing}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                opacity: 0.6,
              }
            }}
          >
            {isProcessing ? copy.generating : copy.aiAssistant}
          </Button>
        </Tooltip>
      )}

      {/* AI Improvement Feedback */}
      {showCritic && (
        <Tooltip title={copy.criticTooltip}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PsychologyIcon />}
            onClick={onOpenCritic}
            disabled={isProcessing}
          >
            {copy.critic}
          </Button>
        </Tooltip>
      )}
    </Box>
  );
};

// `not_mapped`, nicht `unmapped`: der Wert geht als `omop_status`-Filter an
// `getParameters` (catalogService.js, `appendValue('omop_status', ...)`) und
// muss demselben Wert entsprechen, den FilterExpansionPanel.js/FilterDrawer.js/
// CompactFilterBar.js fuer denselben Filter bereits verwenden -- `unmapped`
// ist dort nirgends belegt und liefert am echten Backend keine Treffer (#96
// Rueckmeldung Runde 2, NEU-B1). Der Status-Feldwert einzelner Mappings
// (`omopMapping.status`) ist ein anderer Wertebereich und bleibt `unmapped`
// (OmopMappingDetail.js, ParameterDetail.js) -- die Verwechslung der beiden
// war der eigentliche Fehler.
const UNMAPPED_OMOP_STATUS = 'not_mapped';
const DEFAULT_COST_CAP = '20';

/**
 * OmopMappingBatchStartDialog (#96)
 * Startet einen Mapping-Batch ueber eine Parameterauswahl (Filter oder alle
 * ungemappten Parameter), eine Modellwahl aus den aktiven Modellen und einen
 * Kostendeckel. Zeigt keinen Fortschritt (Ticket 5, JobMonitor.js) -- nach
 * dem Start schliesst der Dialog, der Fortschritt ist im Job-Monitor zu
 * sehen.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {function} [props.onStarted] - Callback mit dem erzeugten Job
 */
export const OmopMappingBatchStartDialog = ({ open, onClose, onStarted }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [mode, setMode] = useState('unmapped');
  const [filterQuery, setFilterQuery] = useState('');
  const [parameters, setParameters] = useState([]);
  const [parametersLoading, setParametersLoading] = useState(false);
  const [parametersError, setParametersError] = useState('');

  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');

  const [costCap, setCostCap] = useState(DEFAULT_COST_CAP);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');

  const activeModels = models.filter((model) => model.active);
  const hasActiveModel = activeModels.length > 0;

  const loadParameters = async (filters) => {
    setParametersLoading(true);
    setParametersError('');
    const { parameters: found, source } = await getParameters(filters);
    setParametersLoading(false);
    if (source === 'error') {
      setParameters([]);
      setParametersError(t('mappingBatchDialog.parametersLoadError'));
      return;
    }
    setParameters(found);
  };

  // Ein wiedergeoeffneter Dialog darf nicht den Stand des letzten Laufs
  // zeigen -- Auswahl, Modell und Fehler werden bei jedem Oeffnen neu geholt
  // statt aus dem vorherigen Zustand weiterverwendet. Die Modellvorauswahl
  // passiert direkt in der Antwort, nicht in einem zweiten Effekt mit
  // `models` als Abhaengigkeit (#96 Rueckmeldung Runde 1, Blocker B6):
  // liefert `getLlmModels` beim Wiederoeffnen dieselbe Array-Referenz wie
  // beim letzten Mal (aktive Modelle unveraendert), aendert sich `models`
  // aus React-Sicht nicht -- ein Effekt, der nur auf diese Referenz lauscht,
  // feuert dann nie wieder und die Vorauswahl bleibt leer.
  useEffect(() => {
    if (!open) {
      return;
    }
    setMode('unmapped');
    setFilterQuery('');
    setSelectedModelId('');
    setCostCap(DEFAULT_COST_CAP);
    setStartError('');
    loadParameters({ omopStatus: UNMAPPED_OMOP_STATUS });

    setModelsLoading(true);
    setModelsError('');
    getLlmModels()
      .then((data) => {
        const fetchedModels = Array.isArray(data) ? data : [];
        setModels(fetchedModels);
        const firstActive = fetchedModels.find((model) => model.active);
        if (firstActive) {
          setSelectedModelId(String(firstActive.id));
        }
      })
      .catch(() => setModelsError(t('mappingBatchDialog.modelsLoadError')))
      .finally(() => setModelsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleModeChange = (event) => {
    const nextMode = event.target.value;
    setMode(nextMode);
    if (nextMode === 'unmapped') {
      loadParameters({ omopStatus: UNMAPPED_OMOP_STATUS });
    } else {
      setParameters([]);
    }
  };

  const handleApplyFilter = () => {
    loadParameters({ q: filterQuery });
  };

  const costCapNumber = Number(costCap);
  const hasValidCostCap = costCap.trim() !== '' && Number.isFinite(costCapNumber) && costCapNumber > 0;
  const canStart =
    !starting && hasActiveModel && Boolean(selectedModelId) && parameters.length > 0 && hasValidCostCap;

  const handleStart = async () => {
    if (!canStart) {
      return;
    }
    setStarting(true);
    setStartError('');
    try {
      // Feldname `sourceParameterIds`: die Plural-Form des einzigen im Repo
      // belegten Nutzlast-Felds fuer diesen Endpunkt (`sourceParameterId`,
      // mappingJobsService.test.js) -- kein eingefrorener Contract, aber die
      // einzige verfuegbare Evidenz (#96 Rueckmeldung Runde 1, Blocker B2).
      const job = await createMappingJob({
        sourceParameterIds: parameters.map((parameter) => getParameterLookupId(parameter)),
        modelId: selectedModelId,
        budget: costCapNumber,
      });
      if (onStarted) {
        onStarted(job);
      }
      if (onClose) {
        onClose();
      }
      // AK4: nach dem Start wird auf den Job-Monitor verwiesen -- der Dialog
      // selbst zeigt keinen Fortschritt (Nicht-Ziel), navigiert aber zur
      // Route, die ihn einbettet (#96 Rueckmeldung Runde 1, Blocker B3).
      if (job && job.id !== undefined && job.id !== null) {
        navigate(`/olymp/mapping-jobs/${job.id}`);
      }
    } catch (error) {
      setStartError(error.message || t('mappingBatchDialog.startError'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onClose={starting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('mappingBatchDialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <RadioGroup row value={mode} onChange={handleModeChange}>
            <FormControlLabel value="unmapped" control={<Radio />} label={t('mappingBatchDialog.modeUnmapped')} />
            <FormControlLabel value="filter" control={<Radio />} label={t('mappingBatchDialog.modeFilter')} />
          </RadioGroup>

          {mode === 'filter' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                label={t('mappingBatchDialog.filterLabel')}
                value={filterQuery}
                onChange={(event) => setFilterQuery(event.target.value)}
              />
              <Button onClick={handleApplyFilter} disabled={parametersLoading}>
                {t('mappingBatchDialog.filterApply')}
              </Button>
            </Box>
          )}

          {parametersLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">{t('mappingBatchDialog.parametersLoading')}</Typography>
            </Box>
          ) : parametersError ? (
            <Alert severity="warning">{parametersError}</Alert>
          ) : (
            <Typography variant="body2">
              {t('mappingBatchDialog.selectedCount', { count: parameters.length })}
            </Typography>
          )}

          {modelsError && <Alert severity="warning">{modelsError}</Alert>}

          {!modelsLoading && !hasActiveModel && !modelsError && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => navigate('/settings')}>
                  {t('mappingBatchDialog.goToSettings')}
                </Button>
              }
            >
              {t('mappingBatchDialog.noActiveModel')}
            </Alert>
          )}

          <FormControl size="small" fullWidth disabled={modelsLoading || !hasActiveModel}>
            <InputLabel id="mapping-batch-model-label">{t('mappingBatchDialog.modelLabel')}</InputLabel>
            <Select
              labelId="mapping-batch-model-label"
              label={t('mappingBatchDialog.modelLabel')}
              value={selectedModelId}
              onChange={(event) => setSelectedModelId(event.target.value)}
            >
              {activeModels.map((model) => (
                <MenuItem key={model.id} value={String(model.id)}>
                  {model.label || model.model}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="number"
            label={t('mappingBatchDialog.costCapLabel')}
            value={costCap}
            onChange={(event) => setCostCap(event.target.value)}
            inputProps={{ min: 0.01, step: 0.01 }}
          />

          <Typography variant="caption" color="text.secondary">
            {t('mappingBatchDialog.jobMonitorHint')}
          </Typography>

          {startError && <Alert severity="error">{startError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={starting}>
          {t('mappingBatchDialog.cancel')}
        </Button>
        <Button variant="contained" onClick={handleStart} disabled={!canStart}>
          {starting ? t('mappingBatchDialog.starting') : t('mappingBatchDialog.start')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OmopMappingActions;
